import { sumDecimalStrings } from '@/lib/decimal';
import type { TargetActual, TargetAttribution } from '@/lib/types';

/**
 * WHAT ONE PERSON CONTRIBUTED TO A TEAM'S TARGET — the whole arithmetic of the
 * Management Dashboard, in one pure module.
 *
 * ── THE DENOMINATOR, AND WHY IT IS THE TEAM'S OWN TARGET ────────────────────
 *
 * The owner asked for "each individual's contribution as a % of the team
 * target", and that is exactly what this computes:
 *
 *     contribution = member's actual ÷ THE TEAM'S OWN targetAmount
 *
 * Two candidates were considered and one was rejected:
 *
 *   the team's own `targetAmount`   ✅ a single figure, printed beside the
 *                                      percentage, that a reader can check
 *   Σ of the members' targets       ❌ a number appearing NOWHERE else on the
 *                                      screen, differing from the team target
 *                                      whenever a group target is not the sum of
 *                                      its members' — which is the normal case,
 *                                      because a group target is a commitment
 *                                      somebody made for the team and the member
 *                                      targets are commitments made for people
 *
 * ⚠️ **THE PERCENTAGES DO NOT SUM TO 100, AND THAT IS THE POINT RATHER THAN A
 * DEFECT.** With the team target as the denominator they sum to the TEAM'S OWN
 * ATTAINMENT — 70% of a target delivered is five members summing to 70%, not to
 * 100%. A denominator of "what the team actually achieved" would force them to
 * 100% and would then answer a different question: *who did the work we did*,
 * rather than *how much of what we promised did each person deliver*. The second
 * is the one asked for, and it is the one where a shortfall is visible.
 *
 * So `membersPctTotal` and `teamPct` are BOTH returned, and the caller shows
 * both. Where they disagree, the difference is real money and `residual` names
 * it.
 *
 * ── ⚠️ THREE STATES IN WHICH A CONTRIBUTION IS NOT COMPUTABLE AT ALL ────────
 *
 * Every one of them must render as a refusal to answer, never as `0%`:
 *
 *   unattributable   the team is a user group naming no relationship any
 *                    revenue document records. The server returns `actual: null`
 *                    on every metric and composes the sentence saying why. There
 *                    is nothing to divide and nothing to divide INTO.
 *
 *   incomparable     the team is a `client_success_manager` group. This is the
 *                    subtle one and it is invisible from the response — see
 *                    below.
 *
 *   no-target        nobody committed a figure for the team. `x% of nothing` is
 *                    undefined, not a smaller number, exactly as a tile's own
 *                    attainment bar already refuses to draw.
 *
 * ── 🔴 WHY A CSM GROUP CANNOT BE BROKEN DOWN BY MEMBER ──────────────────────
 *
 * **A per-USER figure is always account-lead based and there is no other kind.**
 * Measured in the backend's `targets.repository.ts:1031-1041`, which is the one
 * place the predicate is chosen:
 *
 *     user                          COALESCE(doc.account_lead_id,
 *                                             company.account_lead_id) = :id
 *     group via account_lead        the same COALESCE, IN (members)
 *     group via client_success_manager
 *                                   company.client_success_manager_id IN (members)
 *
 * So for an `account_lead` group the member figures and the team figure count
 * the SAME population and divide meaningfully. For a CSM group they count two
 * different populations: the team figure is documents whose company names a
 * member as CSM, and each member's figure is documents that member LEADS. A
 * person can be a client success manager on fifty accounts and lead none of
 * them, and their contribution would read 0% against a team figure they are
 * most of.
 *
 * ⚠️ **NOTHING IN THE RESPONSE FLAGS THIS.** Both queries return a real
 * `actual`, both are internally consistent, and the arithmetic completes. The
 * only signal is `attribution.via`, which says which relationship the TEAM was
 * measured through — and a percentage computed anyway would be a plausible
 * number, on a dashboard, about somebody's performance. That is why it is
 * refused here rather than annotated at the call site.
 *
 * **A per-CSM figure would need `subjectType: 'user'` to accept a relationship,
 * which it does not. This is an API gap, not a rendering decision.**
 *
 * ── ⚠️ `unattributed` IS ORGANISATION-ONLY, AND ITS ABSENCE IS NOT A ZERO ───
 *
 * Documents whose account lead resolves to nobody are in NO member's figure, so
 * the members cannot sum to the whole even when everything else is right. The
 * server reports that subset **only on an organisation query**
 * (`targets.repository.ts:1088`) — it is `null` for a user and `null` for a
 * group, deliberately, because a group total is already narrowed to its members
 * and the subset would read `0.00` for ever, which is a positive claim the query
 * never made.
 *
 * So on an organisation subject this is a real number the page must show, and on
 * a group subject it is genuinely unavailable. **`null` renders as nothing, and
 * never as `0`.**
 */

/** Whether a contribution percentage can be computed, and against what. */
export type ContributionBasis =
  | {
      kind: 'team-target';
      /** The exact decimal the percentages are divided by. **Show it.** */
      denominator: string;
      currency: string | null;
    }
  /** The server declined to compute the team figure. `reason` is ITS sentence. */
  | { kind: 'unattributable'; reason: string }
  /** A CSM group: member figures and the team figure count different documents. */
  | { kind: 'incomparable' }
  /** Nobody set a team target for this metric. */
  | { kind: 'no-target' };

/** Why a member's own figure is missing, when it is. */
export type MemberFigureStatus = 'loading' | 'ok' | 'refused' | 'error';

export interface MemberContributionInput {
  userId: number;
  name: string;
  status: MemberFigureStatus;
  /** The member's own row for this metric, when it was read. */
  row: TargetActual | undefined;
  /** The server's sentence for a refusal or a failure. */
  message: string | null;
}

export interface MemberContribution {
  userId: number;
  name: string;
  status: MemberFigureStatus;
  message: string | null;
  /**
   * The member's actual as an exact decimal string.
   *
   * ⚠️ **`null` means NOT KNOWN, never zero.** A refused member (no audit grant)
   * and a member who closed nothing are different facts, and only one of them is
   * a performance figure.
   */
  actual: string | null;
  /** Documents left out of this member's figure for their currency. */
  excludedByCurrency: number | null;
  /**
   * The member's share of the team target, unrounded percent.
   *
   * `null` whenever the basis is not `team-target` or the actual is unknown.
   * **Never coalesce it to 0.**
   */
  pct: number | null;
}

export interface ContributionRoll {
  basis: ContributionBasis;
  members: MemberContribution[];
  /**
   * Σ of the members' actuals, exact.
   *
   * ⚠️ **`null` unless EVERY member's figure resolved.** `sumDecimalStrings`
   * skips nulls rather than counting them as zero, which is right for a basket
   * and wrong here: a sum missing two refused people is not a smaller total, it
   * is not a total. A partial sum shown beside the team figure would invent a
   * residual that is really just the people we could not read.
   */
  membersTotal: string | null;
  /** The team's own actual for this metric; `null` when unmeasurable. */
  teamActual: string | null;
  /**
   * `teamActual − membersTotal` — the team's own figure that no listed member
   * accounts for. `null` unless both sides are known.
   *
   * On an `account_lead` group this should be `0.00`; anything else means the
   * member list and the group's membership disagree, which is worth seeing.
   */
  residual: string | null;
  /** The team's attainment against its own target. Unclamped, unrounded. */
  teamPct: number | null;
  /**
   * Σ of the members' percentages — the number that must be compared with
   * `teamPct` rather than with 100.
   */
  membersPctTotal: number | null;
}

/** The exact negation of a decimal string, for the one subtraction here. */
function negate(value: string): string {
  const trimmed = value.trim();
  return trimmed.startsWith('-') ? trimmed.slice(1) : `-${trimmed}`;
}

/**
 * A percentage, or `null` where the question is not askable.
 *
 * ⚠️ Guarded on the denominator being a POSITIVE finite number, not merely
 * non-null. A target of zero divides to `Infinity`, which renders as
 * "Infinity% of target" — and zero is a legal thing to commit to. Same guard the
 * attainment tile already applies, and it is written twice on purpose: the tile
 * divides an actual by a target and this divides a person's actual by a team's,
 * so a shared helper would join two rules that are only coincidentally alike.
 */
function pctOf(amount: string | null, denominator: string): number | null {
  if (amount === null) return null;
  const top = Number(amount);
  const bottom = Number(denominator);
  if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom <= 0) return null;
  return (top / bottom) * 100;
}

/**
 * WHICH BASIS APPLIES, from the team's attribution and its target.
 *
 * ⚠️ **The order of these tests is the contract.** `unattributable` first,
 * because a team whose figure cannot be computed has no comparable denominator
 * whatever its target says — the brief is explicit that a contribution is not
 * computable there, and a target set against such a group is a real number that
 * would happily divide into a plausible lie.
 *
 * ⚠️ **An unrecognised `attribution.kind` falls to `no-target` rather than to a
 * percentage.** Unknown means the contract is newer than this build, and the
 * same rule `xero.ts`'s refusal map and `line-project.ts`'s `kind: 'unknown'`
 * already follow: say nothing about measurability rather than defaulting to an
 * answer.
 */
export function contributionBasis(
  attribution: TargetAttribution | null,
  teamRow: TargetActual | undefined,
): ContributionBasis {
  if (attribution !== null && attribution.kind === 'unattributable') {
    return { kind: 'unattributable', reason: attribution.reason };
  }
  if (attribution !== null && attribution.kind === 'group' && attribution.via !== 'account_lead') {
    return { kind: 'incomparable' };
  }
  if (attribution === null || (attribution.kind !== 'organisation' && attribution.kind !== 'group')) {
    return { kind: 'no-target' };
  }
  if (teamRow === undefined || teamRow.target === null) return { kind: 'no-target' };
  return { kind: 'team-target', denominator: teamRow.target, currency: teamRow.baseCurrency };
}

/** Every member's share, plus the reconciliation that says what is missing. */
export function rollUpContributions(
  basis: ContributionBasis,
  teamRow: TargetActual | undefined,
  inputs: readonly MemberContributionInput[],
): ContributionRoll {
  const members: MemberContribution[] = inputs.map((input) => {
    /*
     * ⚠️ **A member's actual is only a figure when the request SUCCEEDED.** A
     * refused or failed read leaves `row` undefined, and reading `row?.actual`
     * without the status test would be indistinguishable from a real answer the
     * moment a server started returning partial rows.
     */
    const actual = input.status === 'ok' ? (input.row?.actual ?? null) : null;
    return {
      userId: input.userId,
      name: input.name,
      status: input.status,
      message: input.message,
      actual,
      excludedByCurrency: input.status === 'ok' ? (input.row?.excludedByCurrency ?? null) : null,
      pct: basis.kind === 'team-target' ? pctOf(actual, basis.denominator) : null,
    };
  });

  /*
   * ⚠️ **Every member's figure, or no sum at all.** See `membersTotal`.
   */
  const everyMemberKnown = members.length > 0 && members.every((m) => m.actual !== null);
  const membersTotal = everyMemberKnown ? sumDecimalStrings(members.map((m) => m.actual)) : null;

  const teamActual = teamRow?.actual ?? null;
  const residual =
    teamActual !== null && membersTotal !== null
      ? sumDecimalStrings([teamActual, negate(membersTotal)])
      : null;

  const teamPct = basis.kind === 'team-target' ? pctOf(teamActual, basis.denominator) : null;
  const membersPctTotal =
    basis.kind === 'team-target' && everyMemberKnown
      ? members.reduce((running, m) => running + (m.pct ?? 0), 0)
      : null;

  return { basis, members, membersTotal, teamActual, residual, teamPct, membersPctTotal };
}
